import React from 'react'

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'
import { useStableCallback } from './useStableCallback'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useImperativeInterval<T extends () => any>(
  callback: T,
  duration: number
): { set: (duration?: number) => void; clear: () => void | undefined } {
  const options = React.useRef<{ callback: T; duration: number }>({
    callback,
    duration
  })
  const interval = React.useRef<ReturnType<typeof setInterval>>()

  // Remember the latest callback and duration.
  useIsomorphicLayoutEffect(() => {
    options.current.callback = callback
    options.current.duration = duration
  }, [callback, duration])

  // this handle clears the interval
  const clear = useStableCallback(
    () => interval.current && clearInterval(interval.current)
  )

  // this handle sets our interval
  const set = useStableCallback(
    (newDuration: number = options.current.duration) => {
      // but clears the old one first
      clear()
      interval.current = setInterval(() => {
        typeof options.current.callback === 'function' &&
          options.current.callback()
      }, newDuration)
    }
  )
  useIsomorphicLayoutEffect(() => clear, [clear])

  // set & clear shouldn't be called in render
  return { set, clear }
}
